/**
 * Epoch-seconds time helpers for intraday charts (Lightweight Charts `UTCTimestamp`).
 *
 * Lightweight Charts treats numeric times as UTC epoch SECONDS and, left alone, labels them
 * in UTC. TekInvest's exchange clock is IST, so the chart keeps true epoch seconds as its data
 * and only the DISPLAY is converted, with `Intl` and an explicit IANA zone. Nothing depends on
 * the viewer's browser timezone.
 */

export const IST_ZONE = "Asia/Kolkata";
/** The backend's naive timestamps are IST wall-clock (see `trade_review.iso_ist`). */
const IST_OFFSET = "+05:30";

const ISO =
  /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)(Z|[+-]\d{2}:?\d{2})?$/;

/**
 * ISO-8601 timestamp -> UTC epoch seconds (floored), or null when unparseable.
 *  - a value WITH an offset / "Z" is honoured exactly (the trade-review API sends `+05:30`);
 *  - a value WITHOUT one is read as IST wall-clock, the platform's naive-timestamp convention,
 *    never as the viewer's local time.
 */
export function isoToEpochSeconds(value: string | null | undefined): number | null {
  if (typeof value !== "string") return null;
  const m = ISO.exec(value.trim());
  if (!m) return null;
  const time = m[2].length === 5 ? `${m[2]}:00` : m[2];
  const ms = Date.parse(`${m[1]}T${time}${m[3] ?? IST_OFFSET}`);
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
}

const formatters = new Map<string, Intl.DateTimeFormat>();
function parts(epochSeconds: number, zone: string): Record<string, string> {
  let f = formatters.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-GB", {
      timeZone: zone,
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    formatters.set(zone, f);
  }
  const out: Record<string, string> = {};
  for (const p of f.formatToParts(new Date(epochSeconds * 1000))) out[p.type] = p.value;
  return out;
}

/** "HH:mm" in `zone`. */
export function formatZonedTime(epochSeconds: number, zone: string = IST_ZONE): string {
  const p = parts(epochSeconds, zone);
  return `${p.hour}:${p.minute}`;
}

/** Crosshair label, e.g. "15 Oct 2026 09:15 IST". */
export function formatZonedDateTime(epochSeconds: number, zone: string = IST_ZONE): string {
  const p = parts(epochSeconds, zone);
  const suffix = zone === IST_ZONE ? " IST" : "";
  return `${p.day} ${p.month} ${p.year} ${p.hour}:${p.minute}${suffix}`;
}

/** Lightweight Charts `TickMarkType` numeric values (stable enum): Year 0, Month 1, DayOfMonth 2, Time 3, TimeWithSeconds 4. */
export function formatZonedTick(epochSeconds: number, tickMarkType: number, zone: string = IST_ZONE): string {
  const p = parts(epochSeconds, zone);
  switch (tickMarkType) {
    case 0:
      return p.year;
    case 1:
      return p.month;
    case 2:
      return `${p.day} ${p.month}`;
    case 4:
      return `${p.hour}:${p.minute}:${p.second}`;
    default:
      return `${p.hour}:${p.minute}`;
  }
}
